import React, { useState } from 'react';
import {
    Box, Paper, Typography, IconButton, Tooltip,
    Switch, FormControlLabel, Snackbar, Alert, CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import QueueIcon from '@mui/icons-material/Queue';
import WavesIcon from '@mui/icons-material/Waves';
import SettingsIcon from '@mui/icons-material/Settings';
import { setKeyValueV2 } from '../../../api/admin';
import { useFirebase } from '../../../context/FirebaseContext';

const StatCard = ({ icon, label, value, subValue, color = 'primary' }) => (
    <Paper className="alpha-stat-card">
        <Box className="alpha-stat-icon" sx={{ color: `${color}.main` }}>
            {icon}
        </Box>
        <Box className="alpha-stat-content">
            <Typography variant="h4" className="alpha-stat-value">
                {value ?? '-'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {label}
            </Typography>
            {subValue && (
                <Typography variant="caption" color="textSecondary">
                    {subValue}
                </Typography>
            )}
        </Box>
    </Paper>
);

const AlphaStatsPanel = ({ stats, onRefresh, loading }) => {
    const { auth } = useFirebase();
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    if (!stats) {
        return null;
    }

    const handleToggle = async (key, currentValue) => {
        setSaving(true);
        try {
            const newValue = currentValue ? 'false' : 'true';
            const response = await new Promise((resolve) => {
                setKeyValueV2(auth, resolve, {
                    key: key,
                    value: newValue,
                    description: `Alpha system setting: ${key}`
                });
            });

            if (response && !response.error) {
                setSnackbar({
                    open: true,
                    message: `${key} updated to ${newValue}`,
                    severity: 'success'
                });
                // Refresh stats to get updated values
                if (onRefresh) onRefresh();
            } else {
                throw new Error(response?.error || 'Failed to update');
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Failed to update ${key}`,
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box className="alpha-stats-panel">
            <Box className="alpha-stats-header">
                <Typography variant="h6">Alpha Access Overview</Typography>
                <Box className="alpha-stats-actions">
                    <Tooltip title="Refresh stats">
                        <IconButton onClick={onRefresh} disabled={loading} size="small">
                            <RefreshIcon className={loading ? 'alpha-spin' : ''} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Alpha Settings Panel */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SettingsIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight="600">
                        Alpha System Settings
                    </Typography>
                    {saving && <CircularProgress size={16} />}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={stats.alphaEnabled || false}
                                onChange={() => handleToggle('alpha.enabled', stats.alphaEnabled)}
                                disabled={saving}
                                color="success"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Alpha Enabled</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Master switch for alpha access
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={stats.inviteRequired || false}
                                onChange={() => handleToggle('alpha.invite_required', stats.inviteRequired)}
                                disabled={saving || !stats.alphaEnabled}
                                color="warning"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Invite Required</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Require invite code for new users
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={stats.waitlistEnabled || false}
                                onChange={() => handleToggle('alpha.waitlist_enabled', stats.waitlistEnabled)}
                                disabled={saving || !stats.alphaEnabled}
                                color="info"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Waitlist Enabled</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Allow users to join waitlist
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={stats.referralEnabled || false}
                                onChange={() => handleToggle('alpha.referral_enabled', stats.referralEnabled)}
                                disabled={saving || !stats.alphaEnabled}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">Referrals</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    User referral codes
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Paper>

            <Box className="alpha-stats-grid">
                <StatCard
                    icon={<PeopleIcon />}
                    label="Alpha Users"
                    value={stats.totalAlphaUsers}
                    color="success"
                />
                <StatCard
                    icon={<VpnKeyIcon />}
                    label="Active Codes"
                    value={stats.activeCodesCount}
                    subValue={`${stats.totalCodesUsed || 0} redeemed`}
                    color="info"
                />
                <StatCard
                    icon={<QueueIcon />}
                    label="Waitlist"
                    value={stats.totalWaitlist}
                    subValue={`${stats.waitlistInvited || 0} pending invites`}
                    color="warning"
                />
                <StatCard
                    icon={<WavesIcon />}
                    label="Active Waves"
                    value={stats.activeWavesCount}
                    subValue={`${stats.totalWaves || 0} total`}
                    color="primary"
                />
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AlphaStatsPanel;

